import { ref, set, get, push, remove, update, query, orderByChild, limitToLast } from 'firebase/database';
import { database } from './firebase';

export const databaseService = {
  // Generic CRUD operations
  create: async (path, data) => {
    try {
      const newRef = push(ref(database, path));
      await set(newRef, {
        ...data,
        id: newRef.key,
        createdAt: Date.now()
      });
      return newRef.key;
    } catch (error) {
      throw error;
    }
  },

  read: async (path) => {
    try {
      const snapshot = await get(ref(database, path));
      return snapshot.exists() ? snapshot.val() : null;
    } catch (error) {
      throw error;
    }
  },

  update: async (path, data) => {
    try {
      await update(ref(database, path), {
        ...data,
        updatedAt: Date.now()
      });
    } catch (error) {
      throw error;
    }
  },

  delete: async (path) => {
    try {
      await remove(ref(database, path));
    } catch (error) {
      throw error;
    }
  },

  // Specific operations for gallery
  getGalleryItems: async (category = null) => {
    try {
      const galleryRef = ref(database, 'website/gallery/items');
      const snapshot = await get(galleryRef);

      if (!snapshot.exists()) return [];

      const items = snapshot.val();
      let itemsArray = Object.values(items);

      if (category && category !== 'all') {
        itemsArray = itemsArray.filter(item => item.category === category);
      }

      return itemsArray.filter(item => item.published).sort((a, b) => a.displayOrder - b.displayOrder);
    } catch (error) {
      throw error;
    }
  },

  // Get all gallery items (including unpublished) for admin
  getAllGalleryItems: async () => {
    try {
      const galleryRef = ref(database, 'website/gallery/items');
      const snapshot = await get(galleryRef);

      if (!snapshot.exists()) return [];

      const items = snapshot.val();
      return Object.values(items).sort((a, b) => b.createdAt - a.createdAt);
    } catch (error) {
      throw error;
    }
  },

  // Get gallery categories
  getGalleryCategories: async () => {
    try {
      const categoriesRef = ref(database, 'website/gallery/categories');
      const snapshot = await get(categoriesRef);

      if (!snapshot.exists()) return [];

      const categories = snapshot.val();
      return Object.values(categories).sort((a, b) => a.order - b.order);
    } catch (error) {
      throw error;
    }
  },

  // Get contact submissions
  getContactSubmissions: async (limit = 50) => {
    try {
      const submissionsRef = ref(database, 'website/contactSubmissions');
      const q = query(submissionsRef, orderByChild('submittedAt'), limitToLast(limit));
      const snapshot = await get(q);

      if (!snapshot.exists()) return [];

      return Object.values(snapshot.val()).sort((a, b) => b.submittedAt - a.submittedAt);
    } catch (error) {
      throw error;
    }
  },

  // Submit contact form
  submitContactForm: async (formData) => {
    try {
      const submissionData = {
        ...formData,
        submittedAt: Date.now(),
        read: false
      };

      return await databaseService.create('website/contactSubmissions', submissionData);
    } catch (error) {
      throw error;
    }
  },

  // Mark submission as read
  markSubmissionAsRead: async (submissionId, read = true) => {
    try {
      await update(ref(database, `website/contactSubmissions/${submissionId}`), { read });
    } catch (error) {
      throw error;
    }
  },

  // Get company info
  getCompanyInfo: async () => {
    return await databaseService.read('website/company');
  },

  // Get about page data
  getAboutPage: async () => {
    return await databaseService.read('website/aboutPage');
  },

  // Get team members
  getTeamMembers: async () => {
    try {
      const teamRef = ref(database, 'website/aboutPage/teamMembers');
      const snapshot = await get(teamRef);

      if (!snapshot.exists()) return [];

      const members = snapshot.val();
      return Object.values(members).sort((a, b) => a.order - b.order);
    } catch (error) {
      throw error;
    }
  },

  // Get page content (privacy/terms)
  getPageContent: async (pageName) => {
    return await databaseService.read(`website/pages/${pageName}`);
  },

  // Team member management
  addTeamMember: async (memberData) => {
    try {
      return await databaseService.create('website/aboutPage/teamMembers', memberData);
    } catch (error) {
      throw error;
    }
  },

  updateTeamMember: async (memberId, memberData) => {
    try {
      await update(ref(database, `website/aboutPage/teamMembers/${memberId}`), {
        ...memberData,
        updatedAt: Date.now()
      });
    } catch (error) {
      throw error;
    }
  },

  deleteTeamMember: async (memberId) => {
    try {
      await remove(ref(database, `website/aboutPage/teamMembers/${memberId}`));
    } catch (error) {
      throw error;
    }
  }
};
